import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useAutoplayVideo from "../hooks/useAutoplayVideo";
import { useLanguage } from "../context/LanguageContext";

export default function VerifyEmailPage() {
  const { t } = useLanguage();
  const { verifyEmail, resendVerification } = useAuth();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") ?? "").trim();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  useAutoplayVideo(videoRef);

  const [status, setStatus] = useState<"loading" | "success" | "invalid">(
    token ? "loading" : "invalid"
  );
  const [resendEmail, setResendEmail] = useState("");
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    verifyEmail(token).then((result) => {
      if (cancelled) return;
      setStatus(result.ok ? "success" : "invalid");
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResend = async (e: FormEvent) => {
    e.preventDefault();
    await resendVerification(resendEmail);
    setResendSent(true);
  };

  if (status === "success") {
    return <Navigate to="/" replace />;
  }

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-ink pb-16 pt-32 md:pt-36">
      <video
        ref={videoRef}
        onLoadedData={() => setIsVideoLoaded(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-engineered ${
          isVideoLoaded ? "opacity-100" : "opacity-0"
        }`}
        src="/assets/video/hero-culinary.mp4"
        preload="auto"
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-black/25" />

      <div className="container-industrial relative z-10">
        <div className="mx-auto grid max-w-5xl grid-cols-1 border border-white/15 bg-black/20 backdrop-blur-md xl:grid-cols-2">
          <div className="hidden flex-col justify-end p-10 xl:flex">
            <h1 className="font-display uppercase text-3xl font-normal tracking-[0.3px] leading-[1.1] text-white xl:text-[2.5rem]">
              BARCRAFT
            </h1>
          </div>

          <div className="flex flex-col justify-center p-8 md:p-12">
            {status === "loading" ? (
              <p className="text-sm leading-relaxed text-white/70">{t("verifyEmailLoading")}</p>
            ) : (
              <div className="flex flex-col gap-5">
                <h2 className="font-display uppercase text-2xl font-normal tracking-[0.3px] text-white">
                  {t("verifyEmailInvalidTitle")}
                </h2>
                <p className="text-sm leading-relaxed text-white/80">
                  {t("verifyEmailInvalidDescription")}
                </p>

                {resendSent ? (
                  <p className="text-sm text-white/70">{t("authResendSentMessage")}</p>
                ) : (
                  <form onSubmit={handleResend} className="flex flex-col gap-3">
                    <p className="text-sm text-white/70">{t("verifyEmailResendIntro")}</p>
                    <input
                      required
                      type="email"
                      value={resendEmail}
                      placeholder="name@company.com"
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="border border-white/25 bg-white/5 px-3 py-2.5 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/60"
                    />
                    <button type="submit" className="btn-primary w-full">
                      {t("authResendVerification")}
                    </button>
                  </form>
                )}

                <Link to="/login" className="text-sm font-semibold text-white/50 hover:text-accent">
                  {t("authBackToLogin")}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
